import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  Briefcase,
  LogOut,
  Menu,
  X,
  Ticket,
  CircleDot,
  CheckCircle2,
  Home,
  User2,
  Calendar,
  MessageSquare,
  KeyRound,
  AlertOctagon,
  Wrench,
  Sparkles,
  HelpCircle,
  Flame,
  TrendingUp,
} from "lucide-react";

const API_BASE = "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api";

const STATUS_STYLES = {
  New: "bg-slate-100 text-slate-700 border border-slate-300",
  Assigned: "bg-blue-100 text-blue-700 border border-blue-300",
  InProgress: "bg-amber-100 text-amber-700 border border-amber-300",
  Resolved: "bg-emerald-100 text-emerald-700 border border-emerald-300",
  Closed: "bg-gray-200 text-gray-600 border border-gray-300",
};

const PRIORITY_BORDER = {
  Low: "border-l-slate-300",
  Medium: "border-l-blue-400",
  High: "border-l-amber-400",
  Critical: "border-l-red-500",
};

const STATUS_LABEL = (s) => (s === "InProgress" ? "In Progress" : s);

const CATEGORY_ICON = {
  SystemDown: <AlertOctagon className="w-4 h-4" />,
  BugNotWorking: <Wrench className="w-4 h-4" />,
  FeatureRequest: <Sparkles className="w-4 h-4" />,
  Other: <HelpCircle className="w-4 h-4" />,
};

const CATEGORY_LABEL = {
  SystemDown: "System Down",
  BugNotWorking: "Not Working",
  FeatureRequest: "Feature Request",
  Other: "Other",
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.New}`}>
      {STATUS_LABEL(status)}
    </span>
  );
}

export default function TeamMemberDashboard() {
  const navigate = useNavigate();
  const [memberData] = useState(() => JSON.parse(sessionStorage.getItem("teamMember") || "null"));

  useEffect(() => {
    if (!memberData) navigate("/TeamMemberLogin");
  }, [memberData, navigate]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // open | all
  const [currentPage, setCurrentPage] = useState(1);
  const TICKETS_PER_PAGE = 5;

  const [resolveModalTicket, setResolveModalTicket] = useState(null);
  const [resolutionMessage, setResolutionMessage] = useState("");
  const [resolving, setResolving] = useState(false);

  const [inProgressUpdating, setInProgressUpdating] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!memberData?.teamMemberId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/tickets/teammember/${memberData.teamMemberId}`);
      if (!res.ok) throw new Error(`Failed to load tickets (${res.status})`);
      setTickets(await res.json());
    } catch (err) {
      toast.error(err.message || "Failed to load your tickets");
    } finally {
      setLoading(false);
    }
  }, [memberData?.teamMemberId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const handleLogout = () => {
    sessionStorage.removeItem("teamMember");
    navigate("/TeamMemberLogin");
  };

  const markInProgress = async (ticket) => {
    setInProgressUpdating(ticket.ticketId);
    try {
      const res = await fetch(`${API_BASE}/tickets/${ticket.ticketId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Status: "InProgress" }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      toast.success(`${ticket.ticketNumber} marked In Progress`);
      fetchTickets();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInProgressUpdating(null);
    }
  };

  const openResolveModal = (ticket) => {
    setResolveModalTicket(ticket);
    setResolutionMessage("");
  };

  const handleResolve = async () => {
    if (!resolutionMessage.trim()) {
      toast.error("Write a message for the person who logged this ticket");
      return;
    }
    setResolving(true);
    try {
      const res = await fetch(`${API_BASE}/tickets/${resolveModalTicket.ticketId}/resolve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ResolutionMessage: resolutionMessage }),
      });
      if (!res.ok) throw new Error(await res.text() || "Failed to resolve ticket");
      toast.success(`${resolveModalTicket.ticketNumber} marked resolved - they've been emailed`);
      setResolveModalTicket(null);
      fetchTickets();
    } catch (err) {
      toast.error(err.message || "Failed to resolve ticket");
    } finally {
      setResolving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Fill in all password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const res = await fetch(`${API_BASE}/teammembers/${memberData.teamMemberId}/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ CurrentPassword: currentPassword, NewPassword: newPassword }),
      });
      if (!res.ok) throw new Error(await res.text() || "Failed to change password");
      toast.success("Password updated - use it next time you sign in");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const visibleTickets = tickets.filter((t) =>
    filter === "all" ? true : t.status !== "Resolved" && t.status !== "Closed"
  );

  const totalPages = Math.max(1, Math.ceil(visibleTickets.length / TICKETS_PER_PAGE));
  const paginatedTickets = visibleTickets.slice(
    (currentPage - 1) * TICKETS_PER_PAGE,
    currentPage * TICKETS_PER_PAGE
  );

  const openCount = tickets.filter((t) => t.status !== "Resolved" && t.status !== "Closed").length;
  const inProgressCount = tickets.filter((t) => t.status === "InProgress").length;
  const urgentCount = tickets.filter(
    (t) => (t.priority === "Critical" || t.priority === "High") && t.status !== "Resolved" && t.status !== "Closed"
  ).length;
  const resolvedCount = tickets.filter((t) => t.status === "Resolved" || t.status === "Closed").length;

  const firstName = (memberData?.name || "").split(" ")[0];

  if (!memberData) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-40 px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-gray-800">My Tickets</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg hover:bg-gray-100 transition">
          {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-gray-900 shadow-xl z-30 transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="px-6 py-8 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{memberData?.name}</h1>
                <p className="text-xs text-gray-500">{memberData?.designation}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1">
            <div className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-800 text-white">
              <Ticket className="w-5 h-5" />
              <span className="text-sm font-medium">My Tickets</span>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition-all duration-200"
            >
              <KeyRound className="w-5 h-5" />
              <span className="text-sm font-medium">Change Password</span>
            </button>
          </nav>

          <div className="p-4 border-t border-gray-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
            <p className="text-[10px] text-gray-600 text-center mt-4">Powered by Basic Intelligent Software (BIS)</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72">
        <div className="p-5 md:p-8 pt-20 lg:pt-8 space-y-6">
          {/* Header Banner */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full -mr-32 -mt-32 opacity-60" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-blue-200 flex-shrink-0">
                  {(memberData?.name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, {firstName}</h1>
                  <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                    <Briefcase className="w-3.5 h-3.5" />
                    {memberData?.designation} &middot; MSA Support
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {openCount === 0
                    ? "You're all caught up"
                    : `${openCount} ticket${openCount === 1 ? "" : "s"} need${openCount === 1 ? "s" : ""} your attention`}
                </p>
                <p className="text-sm font-semibold text-gray-700">{memberData?.email}</p>
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-5 shadow-lg shadow-amber-200 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-50 font-medium">Open</p>
                  <p className="text-3xl font-bold mt-1">{openCount}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl"><CircleDot className="w-6 h-6 text-white" /></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-100 font-medium">In Progress</p>
                  <p className="text-3xl font-bold mt-1">{inProgressCount}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl"><TrendingUp className="w-6 h-6 text-white" /></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-rose-500 to-red-600 rounded-xl p-5 shadow-lg shadow-rose-200 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-rose-100 font-medium">High / Critical</p>
                  <p className="text-3xl font-bold mt-1">{urgentCount}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl"><Flame className="w-6 h-6 text-white" /></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-100 font-medium">Resolved / Closed</p>
                  <p className="text-3xl font-bold mt-1">{resolvedCount}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-xl"><CheckCircle2 className="w-6 h-6 text-white" /></div>
              </div>
            </div>
          </div>

          {/* Ticket list */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg"><Ticket className="w-5 h-5 text-indigo-600" /></div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Assigned to you</h2>
                  <p className="text-xs text-gray-500">{visibleTickets.length} total</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilter("open")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${filter === "open" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  Open
                </button>
                <button
                  onClick={() => setFilter("all")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${filter === "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  All
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3">
              {loading && <p className="text-sm text-gray-500">Loading your tickets...</p>}

              {!loading && visibleTickets.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    {filter === "open" ? (
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    ) : (
                      <Ticket className="w-8 h-8 text-blue-400" />
                    )}
                  </div>
                  <p className="text-gray-700 font-semibold">
                    {filter === "open" ? "All caught up — nice work!" : "No tickets assigned yet"}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {filter === "open" ? "Nothing open right now" : "New assignments will show up here"}
                  </p>
                </div>
              )}

              {!loading && paginatedTickets.map((t) => (
                <div
                  key={t.ticketId}
                  className={`rounded-xl border border-gray-200 border-l-4 ${PRIORITY_BORDER[t.priority] || "border-l-gray-300"} p-4`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="text-xs font-mono text-gray-400">{t.ticketNumber}</span>
                        <StatusPill status={t.status} />
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                          {CATEGORY_ICON[t.category] || <HelpCircle className="w-3.5 h-3.5" />}
                          {CATEGORY_LABEL[t.category] || t.category}
                        </span>
                        <span className="text-xs font-medium text-gray-500">{t.priority} priority</span>
                      </div>
                      <p className="font-semibold text-gray-900">{t.subject}</p>
                      <p className="text-sm text-gray-600 mt-1">{t.description}</p>

                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5 font-medium text-gray-700">
                          <Home className="w-3.5 h-3.5" />
                          {t.residenceName}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <User2 className="w-3.5 h-3.5" />
                          {t.loggedByName}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(t.createdDate).toLocaleDateString()}
                        </span>
                      </div>

                      {t.status === "Resolved" && t.resolutionNotes && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                          <p className="text-xs font-medium text-emerald-700 mb-1">Your resolution message</p>
                          <p className="text-sm text-emerald-800">{t.resolutionNotes}</p>
                        </div>
                      )}
                    </div>

                    {t.status !== "Resolved" && t.status !== "Closed" && (
                      <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                        {t.status !== "InProgress" && (
                          <button
                            onClick={() => markInProgress(t)}
                            disabled={inProgressUpdating === t.ticketId}
                            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition disabled:opacity-50"
                          >
                            {inProgressUpdating === t.ticketId ? "Updating..." : "Start working"}
                          </button>
                        )}
                        <button
                          onClick={() => openResolveModal(t)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-sm transition"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Mark Resolved
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {!loading && visibleTickets.length > TICKETS_PER_PAGE && (
              <div className="flex items-center justify-between px-5 pb-5 pt-1">
                <p className="text-xs text-gray-500">
                  Showing {(currentPage - 1) * TICKETS_PER_PAGE + 1}-
                  {Math.min(currentPage * TICKETS_PER_PAGE, visibleTickets.length)} of {visibleTickets.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Resolve Modal */}
      {resolveModalTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-white">Resolve Ticket</h3>
                <p className="text-xs text-emerald-100">{resolveModalTicket.ticketNumber} - {resolveModalTicket.subject}</p>
              </div>
              <button onClick={() => setResolveModalTicket(null)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  Message to {resolveModalTicket.loggedByName}
                </label>
                <textarea
                  value={resolutionMessage}
                  onChange={(e) => setResolutionMessage(e.target.value)}
                  rows={5}
                  placeholder="Explain what was done to resolve this, so they know exactly what changed..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  This will be emailed to {resolveModalTicket.loggedByEmail} from MSA Support.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setResolveModalTicket(null)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResolve}
                  disabled={resolving}
                  className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium hover:from-emerald-700 hover:to-teal-700 shadow-sm disabled:opacity-50 transition"
                >
                  {resolving ? "Resolving..." : "Resolve & Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-between">
              <h3 className="text-base font-semibold text-white">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium hover:from-blue-700 hover:to-indigo-700 shadow-sm disabled:opacity-50 transition"
                >
                  {changingPassword ? "Saving..." : "Save Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
